import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Reply,
  Forward,
  Link2,
  Plus,
  Paperclip,
  Download,
  Send,
  X,
  Search,
  Building2,
  User,
  Clock,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Mail,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MainNav from "@/components/MainNav";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";
import EmailHtmlFrame from "@/components/EmailHtmlFrame";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GMAIL_ACCOUNTS = [
  "info@alpinearmour.com.au",
  "scott@scottlistondesign.com.au"
];

// Download attachment with authentication
const downloadAttachment = async (messageId, attachmentId, account, filename) => {
  try {
    const token = localStorage.getItem("auth_token");
    const response = await fetch(
      `${API}/gmail/attachment/${messageId}/${attachmentId}?account=${encodeURIComponent(account)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error("Failed to download attachment");
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "attachment";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Download error:", error);
    toast.error("Failed to download attachment");
  }
};

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function extractEmail(fromHeader) {
  const match = fromHeader?.match(/<([^>]+)>/);
  return match ? match[1].toLowerCase() : fromHeader?.toLowerCase().trim() || "";
}

function extractName(fromHeader) {
  const match = fromHeader?.match(/^([^<]+)/);
  if (match) {
    return match[1].trim().replace(/"/g, "");
  }
  return fromHeader?.replace(/<.*>/, "").trim() || "";
}

function MessageCard({ message, expanded, onToggle, isLast }) {
  const senderName = extractName(message.from);
  const senderEmail = extractEmail(message.from);
  const isInternal = GMAIL_ACCOUNTS.includes(senderEmail);
  
  return (
    <div className={`bg-white rounded-lg border ${isInternal ? "border-orange-200" : "border-workspace-border"} ${expanded ? "shadow-md" : ""}`}>
      {/* Header - always visible */}
      <div 
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-workspace-hover"
        onClick={onToggle}
      >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
          isInternal ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-600"
        }`}>
          {senderName.charAt(0).toUpperCase()}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-workspace-text-primary">{senderName}</span>
            {isInternal && (
              <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-600 border-orange-200">
                {senderEmail.split("@")[0]}
              </Badge>
            )}
          </div>
          <p className="text-xs text-gray-500">
            to {message.to?.replace(/<[^>]+>/g, "").trim()}
          </p>
        </div>
        
        <div className="flex items-center gap-2 shrink-0">
          {message.attachments?.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              <Paperclip className="w-3 h-3 mr-1" />
              {message.attachments.length}
            </Badge>
          )}
          <span className="text-xs text-gray-400">{formatDateTime(message.date)}</span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>
      
      {/* Body - expanded only */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Email body — sandboxed so email CSS can't leak into the app */}
          <div className="py-4">
            <EmailHtmlFrame html={message.body_html || message.body_plain?.replace(/\n/g, "<br>") || ""} testid="email-thread-body" />
          </div>
          
          {/* Attachments */}
          {message.attachments?.length > 0 && (
            <div className="border-t border-gray-100 pt-3 mt-3">
              <p className="text-xs font-medium text-gray-500 mb-2">Attachments</p>
              <div className="flex flex-wrap gap-2">
                {message.attachments.map((att, idx) => (
                  <button
                    key={idx}
                    onClick={() => downloadAttachment(message.id, att.id, message.account_email, att.filename)}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md text-sm text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <Paperclip className="w-4 h-4 text-gray-400" />
                    <span className="truncate max-w-[200px]">{att.filename}</span>
                    <Download className="w-3 h-3 text-gray-400" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EmailThread() {
  const { threadId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const account = searchParams.get("account");
  
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMessages, setExpandedMessages] = useState({});
  
  // Reply/Compose state
  const [showCompose, setShowCompose] = useState(false);
  const [composeMode, setComposeMode] = useState("reply"); // reply, forward
  const [composeTo, setComposeTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeFrom, setComposeFrom] = useState(account || GMAIL_ACCOUNTS[0]);
  const [sending, setSending] = useState(false);
  
  // Link to job state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [jobSearch, setJobSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [suggestedJobs, setSuggestedJobs] = useState([]);
  const [linking, setLinking] = useState(false);
  
  // Create job state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newJobName, setNewJobName] = useState("");
  const [creating, setCreating] = useState(false);
  
  const getToken = () => localStorage.getItem("auth_token");
  
  const loadThread = useCallback(async () => {
    if (!threadId || !account) return;
    
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/gmail/thread/${threadId}?account=${encodeURIComponent(account)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setThread(res.data);
      
      // Expand last message by default
      if (res.data.messages?.length > 0) {
        const lastId = res.data.messages[res.data.messages.length - 1].id;
        setExpandedMessages({ [lastId]: true });
      }
    } catch (error) {
      console.error("Failed to load thread:", error);
      toast.error("Failed to load email thread");
    } finally {
      setLoading(false);
    }
  }, [threadId, account]);
  
  useEffect(() => {
    loadThread();
  }, [loadThread]);
  
  useEffect(() => {
    // Load suggested jobs based on sender
    if (thread?.messages?.length > 0) {
      const firstExternal = thread.messages.find(m => {
        const email = extractEmail(m.from);
        return !GMAIL_ACCOUNTS.includes(email);
      });
      
      if (firstExternal) {
        const senderEmail = extractEmail(firstExternal.from);
        loadSuggestedJobs(senderEmail);
      }
    }
  }, [thread]);
  
  const loadSuggestedJobs = async (senderEmail) => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/gmail/suggest-jobs?sender_email=${encodeURIComponent(senderEmail)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSuggestedJobs(res.data.suggestions || []);
    } catch (error) {
      console.error("Failed to load suggested jobs:", error);
    }
  };
  
  const searchJobs = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    try {
      const token = getToken();
      const res = await axios.get(`${API}/gmail/search-jobs?q=${encodeURIComponent(query)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSearchResults(res.data.jobs || []);
    } catch (error) {
      console.error("Failed to search jobs:", error);
    }
  };
  
  const toggleMessage = (messageId) => {
    setExpandedMessages(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };
  
  const handleReply = () => {
    if (!thread?.messages?.length) return;
    
    const lastMsg = thread.messages[thread.messages.length - 1];
    const replyTo = extractEmail(lastMsg.from);
    
    // If last message was from us, reply to the recipient instead
    if (GMAIL_ACCOUNTS.includes(replyTo)) {
      setComposeTo(lastMsg.to?.replace(/<[^>]+>/g, "").trim() || "");
    } else {
      setComposeTo(replyTo);
    }
    
    setComposeSubject(thread.subject.startsWith("Re:") ? thread.subject : `Re: ${thread.subject}`);
    setComposeBody("");
    setComposeMode("reply");
    setShowCompose(true);
  };
  
  const handleForward = () => {
    if (!thread?.messages?.length) return;
    
    const lastMsg = thread.messages[thread.messages.length - 1];
    setComposeTo("");
    setComposeSubject(thread.subject.startsWith("Fwd:") ? thread.subject : `Fwd: ${thread.subject}`);
    setComposeBody(`\n\n---------- Forwarded message ----------\nFrom: ${lastMsg.from}\nDate: ${formatDateTime(lastMsg.date)}\nSubject: ${lastMsg.subject}\n\n${lastMsg.body_plain || ""}`);
    setComposeMode("forward");
    setShowCompose(true);
  };
  
  const handleSend = async () => {
    if (!composeTo.trim() || !composeSubject.trim()) {
      toast.error("Please fill in recipient and subject");
      return;
    }
    
    setSending(true);
    try {
      const token = getToken();
      const lastMsg = thread?.messages?.[thread.messages.length - 1];
      
      await axios.post(`${API}/gmail/send`, {
        to: composeTo,
        subject: composeSubject,
        body_html: composeBody.replace(/\n/g, "<br>"),
        from_account: composeFrom,
        reply_to_thread_id: composeMode === "reply" ? threadId : null,
        in_reply_to: composeMode === "reply" ? lastMsg?.message_id_header : null,
        references: composeMode === "reply" ? lastMsg?.references : null,
        job_id: thread?.linked_job_id || null,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success("Email sent!");
      setShowCompose(false);
      loadThread();
    } catch (error) {
      console.error("Failed to send email:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };
  
  const handleLinkToJob = async (jobId) => {
    setLinking(true);
    try {
      const token = getToken();
      await axios.post(`${API}/gmail/thread/${threadId}/link`, {
        job_id: jobId,
        account: account,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success("Thread linked to job!");
      setShowLinkModal(false);
      loadThread();
    } catch (error) {
      console.error("Failed to link thread:", error);
      toast.error(error.response?.data?.detail || "Failed to link thread");
    } finally {
      setLinking(false);
    }
  };
  
  const handleUnlink = async () => {
    try {
      const token = getToken();
      await axios.delete(`${API}/gmail/thread/${threadId}/link`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success("Thread unlinked");
      loadThread();
    } catch (error) {
      console.error("Failed to unlink thread:", error);
      toast.error("Failed to unlink thread");
    }
  };
  
  const handleCreateJob = async () => {
    setCreating(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API}/gmail/create-job-from-thread`, {
        thread_id: threadId,
        account: account,
        job_name: newJobName || null,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success(`Job ${res.data.job_number} created!`);
      setShowCreateModal(false);
      loadThread();
    } catch (error) {
      console.error("Failed to create job:", error);
      toast.error(error.response?.data?.detail || "Failed to create job");
    } finally {
      setCreating(false);
    }
  };
  
  if (loading) {
    return (
      <WorkspaceLayout title="Loading...">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-workspace-text-muted mx-auto animate-spin" />
        </div>
      </WorkspaceLayout>
    );
  }
  
  if (!thread) {
    return (
      <WorkspaceLayout title="Thread Not Found">
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <Mail className="w-12 h-12 text-workspace-text-muted mx-auto mb-3 opacity-50" />
          <p className="text-workspace-text-muted">Thread not found</p>
          <Link to="/inbox">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Inbox
            </Button>
          </Link>
        </div>
      </WorkspaceLayout>
    );
  }
  
  return (
    <WorkspaceLayout 
      title={thread.subject}
      actions={
        <div className="flex items-center gap-2">
          <Link to="/inbox">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
        </div>
      }
    >
      {/* Thread Header Bar */}
      <div className="bg-white border-b border-workspace-border px-6 py-3">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="text-xs">
              {thread.message_count} message{thread.message_count !== 1 ? "s" : ""}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {account?.split("@")[0]}
            </Badge>
            {thread.linked_job_id && (
              <Link to={`/quote/${thread.linked_job_id}`}>
                <Badge className="text-xs bg-green-100 text-green-700 hover:bg-green-200">
                  <Link2 className="w-3 h-3 mr-1" />
                  {thread.linked_job_number}
                </Badge>
              </Link>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleReply}>
              <Reply className="w-4 h-4 mr-2" />
              Reply
            </Button>
            <Button variant="outline" size="sm" onClick={handleForward}>
              <Forward className="w-4 h-4 mr-2" />
              Forward
            </Button>
            
            {thread.linked_job_id ? (
              <Button variant="outline" size="sm" onClick={handleUnlink}>
                <Link2 className="w-4 h-4 mr-2" />
                Unlink
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowLinkModal(true)}>
                  <Link2 className="w-4 h-4 mr-2" />
                  Link to Job
                </Button>
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => {
                  setNewJobName(thread.subject);
                  setShowCreateModal(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Job
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      
      <main className="max-w-4xl mx-auto px-6 py-6">
        {/* Messages */}
        <div className="space-y-4">
          {thread.messages?.map((message, idx) => (
            <MessageCard
              key={message.id}
              message={message}
              expanded={expandedMessages[message.id]}
              onToggle={() => toggleMessage(message.id)}
              isLast={idx === thread.messages.length - 1}
            />
          ))}
        </div>
        
        {/* Compose Panel */}
        {showCompose && (
          <div className="mt-6 bg-white rounded-lg border border-workspace-border shadow-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b border-workspace-border">
              <h3 className="font-medium text-workspace-text-primary">
                {composeMode === "reply" ? "Reply" : "Forward"}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowCompose(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="p-4 space-y-3">
              {/* From */}
              <div className="flex items-center gap-3">
                <label className="w-16 text-sm text-workspace-text-muted">From:</label>
                <Select value={composeFrom} onValueChange={setComposeFrom}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GMAIL_ACCOUNTS.map(email => (
                      <SelectItem key={email} value={email}>{email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* To */}
              <div className="flex items-center gap-3">
                <label className="w-16 text-sm text-gray-500">To:</label>
                <input
                  type="text"
                  value={composeTo}
                  onChange={(e) => setComposeTo(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
                  placeholder="recipient@example.com"
                />
              </div>
              
              {/* Subject */}
              <div className="flex items-center gap-3">
                <label className="w-16 text-sm text-gray-500">Subject:</label>
                <input
                  type="text"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
                />
              </div>
              
              {/* Body */}
              <textarea
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                rows={10}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30 resize-none"
                placeholder="Write your message..."
              />
              
              <p className="text-xs text-gray-400">
                Signature will be added automatically based on sending account.
              </p>
            </div>
            
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50">
              <Button variant="outline" onClick={() => setShowCompose(false)}>
                Cancel
              </Button>
              <Button onClick={handleSend} disabled={sending} className="bg-aa-primary">
                <Send className="w-4 h-4 mr-2" />
                {sending ? "Sending..." : "Send"}
              </Button>
            </div>
          </div>
        )}
      </main>
      
      {/* Link to Job Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Link to Job</DialogTitle>
            <DialogDescription>
              Search for a job to link this email thread to.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by job number, name, or customer..."
                value={jobSearch}
                onChange={(e) => {
                  setJobSearch(e.target.value);
                  searchJobs(e.target.value);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
              />
            </div>
            
            {/* Suggested Jobs */}
            {suggestedJobs.length > 0 && !jobSearch && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Suggested Jobs</p>
                <div className="space-y-1">
                  {suggestedJobs.map(job => (
                    <button
                      key={job.id}
                      onClick={() => handleLinkToJob(job.id)}
                      disabled={linking}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-aa-primary/5 text-left transition-colors"
                    >
                      <div>
                        <span className="font-mono text-xs text-aa-primary">{job.job_number}</span>
                        <span className="mx-2 text-sm text-gray-800">{job.job_name}</span>
                        <span className="text-xs text-gray-500">{job.client_name}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">{job.status}</Badge>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Search Results</p>
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {searchResults.map(job => (
                    <button
                      key={job.id}
                      onClick={() => handleLinkToJob(job.id)}
                      disabled={linking}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-aa-primary/5 text-left transition-colors"
                    >
                      <div>
                        <span className="font-mono text-xs text-aa-primary">{job.job_number}</span>
                        <span className="mx-2 text-sm text-gray-800">{job.job_name}</span>
                        <span className="text-xs text-gray-500">{job.client_name}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">{job.status}</Badge>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {jobSearch && searchResults.length === 0 && (
              <p className="text-center text-sm text-gray-500 py-4">No jobs found</p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkModal(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Job Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Job from Email</DialogTitle>
            <DialogDescription>
              Create a new job and automatically link this email thread to it.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Job Name</label>
              <input
                type="text"
                value={newJobName}
                onChange={(e) => setNewJobName(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
                placeholder="Enter job name..."
              />
              <p className="text-xs text-gray-400 mt-1">
                Customer info will be extracted from the email sender.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateJob} disabled={creating} className="bg-orange-500 hover:bg-orange-600">
              {creating ? "Creating..." : "Create Job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </WorkspaceLayout>
  );
}
